import {
  Add,
  AddCircleOutline,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  DashboardOutlined,
  SaveOutlined,
  Settings,
} from '@mui/icons-material'
import { Stack, Box, Tooltip, Button, IconButton } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { useIsOmneansConvIntake } from '@omnea-app/fe-chat/useIsOmneansConvIntake'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { ExpandableSidebarLink } from './ExpandableSidebarLink'
import { SidebarLink } from './SidebarLink'
import { SidebarPages } from './SidebarPages'
import { SidebarUncontrolledProps } from './SidebarUncontrolled'
import { SidebarList, SidebarBadge, SidebarDivider, SidebarItemWrapper, SidebarListItemButton } from './StyledSidebarComponents'

type AppSidebarContentProps = Omit<SidebarUncontrolledProps, 'children' | 'path'>

export const AppSidebarContent = ({ isSidebarExpanded, selectedPage, items, numberOfActiveTasks }: AppSidebarContentProps) => {
  const { t } = useTranslation('common')
  const { AI_CHAT_ASSISTANT } = useFeatureFlags()
  const { convIntakeEnabledForCurrentUser } = useIsOmneansConvIntake()

  const router = useRouter()

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack>
        <SidebarList>
          <SidebarLink
            href="/"
            title={t('sidebar.dashboard')}
            selected={selectedPage === SidebarPages.HOME}
            Icon={DashboardOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
          {AI_CHAT_ASSISTANT || convIntakeEnabledForCurrentUser ? (
            <SidebarLink
              href="/ai"
              title={t('sidebar.omnea-ai')}
              selected={selectedPage === SidebarPages.AI}
              Icon={AutoAwesomeOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
          <SidebarLink
            href="/tasks"
            title={t('sidebar.tasks')}
            selected={selectedPage === SidebarPages.TASKS}
            Icon={CheckCircleOutlineOutlined}
            isSidebarExpanded={isSidebarExpanded}
          >
            {numberOfActiveTasks > 0 ? (
              <Tooltip title={t('sidebar.task-no-tooltip', { count: numberOfActiveTasks })}>
                <SidebarBadge sx={!isSidebarExpanded ? { ml: -3.5, mt: -3.5 } : {}}>{numberOfActiveTasks}</SidebarBadge>
              </Tooltip>
            ) : null}
          </SidebarLink>
          <SidebarLink
            href="/start/drafts"
            title={t('sidebar.drafts')}
            selected={selectedPage === SidebarPages.DRAFTS}
            Icon={SaveOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
        </SidebarList>
        <SidebarDivider />
        <SidebarList>
          {items.map((item) => {
            const { children, ...rest } = item
            if (item.children) {
              return <ExpandableSidebarLink {...rest} key={rest.title} items={children} isSidebarExpanded={isSidebarExpanded} />
            }
            return <SidebarLink {...rest} isSidebarExpanded={isSidebarExpanded} key={rest.title} />
          })}
        </SidebarList>
        <SidebarDivider />
      </Stack>
      <Stack sx={{ mt: 'auto' }}>
        <Box sx={{ backgroundColor: 'sidebar.footerBackground' }}>
          <SidebarItemWrapper>
            <SidebarLink
              href={`/settings/notifications?from=${router.asPath}`}
              title={t('sidebar.settings')}
              selected={selectedPage === SidebarPages.SETTINGS_DASHBOARD}
              Icon={Settings}
              isSidebarExpanded={isSidebarExpanded}
            />
          </SidebarItemWrapper>
          <SidebarDivider />
        </Box>
        <Stack spacing={0} height={68} justifyContent="center">
          <SidebarList>
            <Link href="/start" passHref style={{ width: '100%' }}>
              <SidebarListItemButton
                selected={selectedPage === SidebarPages.CREATE_REQUEST}
                onClick={() => track('NewRequestInitiated', { source: 'RequestsCTA' })}
              >
                {isSidebarExpanded ? (
                  <Button variant="contained" sx={{ bgcolor: 'secondary.main', width: '100%' }} startIcon={<Add />}>
                    {t('sidebar.create-request')}
                  </Button>
                ) : (
                  <IconButton sx={{ ml: '-6px' }}>
                    <AddCircleOutline sx={{ color: 'secondary.main' }} />
                  </IconButton>
                )}
              </SidebarListItemButton>
            </Link>
          </SidebarList>
        </Stack>
      </Stack>
    </Stack>
  )
}
